import React, { useEffect, useState, useRef } from 'react';
import { api } from '../services/api';

// ─── tiny helpers ────────────────────────────────────────────────────────────
const toggle = (key, val, setter) =>
  setter(prev => ({ ...prev, [key]: val }));

const Field = ({ label, icon, error, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
      <span>{icon}</span> {label}
    </label>
    {children}
    {error && (
      <p className="text-red-600 text-xs mt-1.5 font-medium flex items-center gap-1 bg-red-50 px-2 py-1.5 rounded-lg">
        ⚠️ {error}
      </p>
    )}
  </div>
);

const Input = ({ className = '', ...props }) => (
  <input
    className={`w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 outline-none transition-all text-slate-800 text-sm ${className}`}
    {...props}
  />
);

const Section = ({ icon, title, children, accent = 'cyan' }) => {
  const gradients = {
    cyan:   'from-cyan-500 to-blue-500',
    indigo: 'from-indigo-500 to-purple-500',
    emerald:'from-emerald-500 to-green-500',
    amber:  'from-amber-400 to-orange-500',
  };
  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm overflow-hidden">
      <div className={`bg-gradient-to-r ${gradients[accent]} px-6 py-4 flex items-center gap-3`}>
        <span className="text-xl">{icon}</span>
        <h3 className="text-white font-bold text-base tracking-wide">{title}</h3>
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </div>
  );
};

const CheckToggle = ({ label, desc, checked, onChange, accent = 'cyan' }) => {
  const colors = {
    cyan:   'peer-checked:bg-cyan-500',
    indigo: 'peer-checked:bg-indigo-500',
    emerald:'peer-checked:bg-emerald-500',
    amber:  'peer-checked:bg-amber-500',
  };
  return (
    <label className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border-2 border-slate-200 cursor-pointer hover:border-cyan-300 transition-all group">
      <div className="flex-1">
        <div className="font-semibold text-slate-800 text-sm group-hover:text-cyan-700 transition-colors">{label}</div>
        {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
      </div>
      <div className="relative ml-4 shrink-0">
        <input type="checkbox" className="peer sr-only" checked={checked} onChange={onChange} />
        <div className={`w-11 h-6 bg-slate-200 rounded-full transition-all ${colors[accent]} after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:shadow after:transition-all peer-checked:after:translate-x-5`} />
      </div>
    </label>
  );
};

// ─── status toast ─────────────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const styles = {
    success: 'bg-emerald-500 text-white',
    error:   'bg-red-500 text-white',
    info:    'bg-cyan-500 text-white',
  };
  const icons = { success: '✅', error: '❌', info: '📨' };
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl ${styles[type]} animate-slideUp`}>
      <span className="text-lg">{icons[type]}</span>
      <span className="font-semibold text-sm">{msg}</span>
      <button onClick={onClose} className="ml-2 opacity-80 hover:opacity-100 text-lg leading-none">✕</button>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────
export default function ReportSettings() {
  const [form, setForm] = useState({
    siteName: '',
    toEmail: '', cc: '', bcc: '',
    hours: '0', minutes: '15', days: '0',
    userName: '', password: '', port: '587',
    smtpServer: '', fromEmail: '', confirmationEmail: '',
    enableSSL: false,
    chkEmployee: false, chkAttendance: false,
    chkDetailed: false, chkZATCA: false,
    zatcaStart: '',
  });

  const [errors, setErrors]     = useState({});
  const [saving, setSaving]     = useState(false);
  const [sending, setSending]   = useState(false);
  const [toast, setToast]       = useState(null);
  const [showPwd, setShowPwd]   = useState(false);
  const [loaded, setLoaded]     = useState(false);
const [sendDate, setSendDate] = useState(new Date().toISOString().split('T')[0]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── load settings on mount ──────────────────────────────────────────────
  useEffect(() => {
    api.get('/report-settings')
      .then(r => {
        const d = r.data || {};
        setForm({
          siteName:          d.SiteName          || '',
          toEmail:           d.ToEmail           || '',
          cc:                d.CC                || '',
          bcc:               d.BCC               || '',
          hours:             String(d.Hours      ?? '0'),
          minutes:           String(d.Minutes    ?? '15'),
          days:              String(d.Days       ?? '0'),
          userName:          d.UserName          || '',
          password:          d.Password          || '',
          port:              String(d.Port       ?? '587'),
          smtpServer:        d.SMTPServer        || '',
          fromEmail:         d.FromEmail         || '',
          confirmationEmail: d.ConfirmationEmail || '',
          enableSSL:         Boolean(d.EnableSSL),
          chkEmployee:       Boolean(d.ChkEmployee),
          chkAttendance:     Boolean(d.ChkAttendance),
          chkDetailed:       Boolean(d.ChkDetailed),
          chkZATCA:          Boolean(d.ChkZATCA),
          zatcaStart: d.ZATCAStart || new Date().toISOString().split('T')[0],
        });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  // ── validation ──────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.siteName.trim())      e.siteName      = 'Site name is required';
    if (!form.toEmail.trim())       e.toEmail       = 'Recipient email is required';
    if (!form.smtpServer.trim())    e.smtpServer    = 'SMTP server is required';
    if (!form.fromEmail.trim())     e.fromEmail     = 'From email is required';
    if (!form.userName.trim())      e.userName      = 'SMTP username is required';
    if (!form.password.trim())      e.password      = 'SMTP password is required';
    const h = parseInt(form.hours, 10);
    const m = parseInt(form.minutes, 10);
    if (isNaN(h) || h < 0 || h > 23) e.hours   = 'Hours must be 0–23';
    if (isNaN(m) || m < 0 || m > 59) e.minutes = 'Minutes must be 0–59';
    if (form.chkZATCA && !form.zatcaStart) e.zatcaStart = 'ZATCA start date is required';
    return e;
  };

  // ── save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setSaving(true);
    try {
      await api.put('/report-settings', {
        ...form,
        hours:   parseInt(form.hours,   10),
        minutes: parseInt(form.minutes, 10),
        days:    parseInt(form.days,    10),
        port:    parseInt(form.port,    10),
      });
      showToast('Settings saved successfully!', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── send now ────────────────────────────────────────────────────────────
  const handleSendNow = async () => {
    setSending(true);
    try {
      await api.post('/report-settings/send-now', sendDate ? { date: sendDate } : {});
      showToast('Reports are being generated and sent by email!', 'info');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to trigger reports', 'error');
    } finally {
      setSending(false);
    }
  };

  const f = (key) => ({
    value: form[key],
    onChange: e => {
      setForm(prev => ({ ...prev, [key]: e.target.value }));
      if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    },
  });

  // ── scheduled time display ──────────────────────────────────────────────
  const scheduledTime = (() => {
    const h = parseInt(form.hours, 10);
    const m = parseInt(form.minutes, 10);
    if (isNaN(h) || isNaN(m)) return '—';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hh = h % 12 || 12;
    return `${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
  })();

  const daysLabel = (() => {
    const d = parseInt(form.days, 10);
    if (d === 0)  return 'Today';
    if (d === -1) return 'Yesterday';
    if (d < 0)   return `${Math.abs(d)} days ago`;
    return `+${d} days`;
  })();

  const enabledReports = [
    form.chkDetailed   && 'Detailed + Alerts',
    form.chkAttendance && 'Attendance',
    form.chkZATCA      && 'ZATCA',
    form.chkEmployee   && 'Employee Sales',
    'Daily Summary',
  ].filter(Boolean);

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cyan-200 border-t-cyan-600 rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading report settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="text-3xl">📧</span>
              <h1 className="text-2xl font-bold tracking-tight">Report Settings</h1>
            </div>
            <p className="text-cyan-100 text-sm mt-1">
              Configure automated daily/monthly email reports from ShortcutsPOS
            </p>
          </div>

          {/* Live schedule badge */}
          <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 text-right border border-white/30">
            <div className="text-xs text-cyan-100 uppercase tracking-wide mb-0.5">Scheduled at</div>
            <div className="text-xl font-bold">{scheduledTime}</div>
            <div className="text-xs text-cyan-200 mt-0.5">{daysLabel}</div>
          </div>
        </div>

        {/* Active reports pills */}
        {enabledReports.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {enabledReports.map(r => (
              <span key={r} className="px-3 py-1 bg-white/20 rounded-full text-xs font-semibold border border-white/30">
                ✓ {r}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── LEFT COL (2/3) ────────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-6">

          {/* General */}
          <Section icon="🏢" title="General" accent="cyan">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Site Name" icon="🏷️" error={errors.siteName}>
                <Input placeholder="e.g. MySalon" {...f('siteName')} />
              </Field>
              <Field label="Days Offset" icon="📅"
                desc="0 = today, -1 = yesterday">
                <div className="flex gap-2">
                  <Input type="number" placeholder="0" {...f('days')} className="text-center font-bold" />
                  <span className="flex items-center px-3 bg-slate-100 border-2 border-slate-200 rounded-lg text-slate-600 text-sm font-medium whitespace-nowrap">
                    {daysLabel}
                  </span>
                </div>
              </Field>
            </div>
          </Section>

          {/* Email Recipients */}
          <Section icon="📬" title="Email Recipients" accent="indigo">
            <Field label="To (Primary Recipient)" icon="📨" error={errors.toEmail}>
              <Input type="email" placeholder="manager@salon.com" {...f('toEmail')} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="CC" icon="📋">
                <Input type="email" placeholder="cc@salon.com" {...f('cc')} />
              </Field>
              <Field label="BCC" icon="🔒">
                <Input type="email" placeholder="bcc@salon.com" {...f('bcc')} />
              </Field>
            </div>
            <Field label="Confirmation Email" icon="✅">
              <Input type="email" placeholder="Receives send-success notification" {...f('confirmationEmail')} />
            </Field>
          </Section>

          {/* SMTP */}
          <Section icon="📡" title="SMTP / Email Server" accent="amber">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="SMTP Server" icon="🖥️" error={errors.smtpServer}>
                <Input placeholder="mail.example.com" {...f('smtpServer')} />
              </Field>
              <Field label="Port" icon="🔌">
                <Input type="number" placeholder="587" {...f('port')} />
              </Field>
              <Field label="Username" icon="👤" error={errors.userName}>
                <Input type="email" placeholder="noreply@salon.com" {...f('userName')} />
              </Field>
              <Field label="Password" icon="🔑" error={errors.password}>
                <div className="relative">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    placeholder="SMTP password"
                    {...f('password')}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-500 transition-colors"
                  >
                    {showPwd ? '🙈' : '👁️'}
                  </button>
                </div>
              </Field>
              <Field label="From Email" icon="📤" error={errors.fromEmail}>
                <Input type="email" placeholder="reports@salon.com" {...f('fromEmail')} />
              </Field>
              <div className="flex items-center">
                <CheckToggle
                  label="Enable SSL/TLS"
                  desc="Use secure connection (port 465 / 587)"
                  checked={form.enableSSL}
                  onChange={e => toggle('enableSSL', e.target.checked, setForm)}
                  accent="amber"
                />
              </div>
            </div>
          </Section>

        </div>

        {/* ── RIGHT COL (1/3) ───────────────────────────────────────────── */}
        <div className="space-y-6">

          {/* Schedule */}
          <Section icon="⏰" title="Schedule" accent="emerald">
            <p className="text-xs text-slate-500 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
              Reports fire automatically once per day at the configured time.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Hour (0–23)" icon="🕐" error={errors.hours}>
                <Input
                  type="number" min={0} max={23}
                  placeholder="0"
                  {...f('hours')}
                  className="text-center text-lg font-bold"
                />
              </Field>
              <Field label="Minute (0–59)" icon="⏱️" error={errors.minutes}>
                <Input
                  type="number" min={0} max={59}
                  placeholder="15"
                  {...f('minutes')}
                  className="text-center text-lg font-bold"
                />
              </Field>
            </div>
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl px-4 py-3 text-center">
              <div className="text-xs text-emerald-600 uppercase tracking-wide font-semibold mb-0.5">Next fire</div>
              <div className="text-2xl font-bold text-emerald-700">{scheduledTime}</div>
            </div>
          </Section>

          {/* Report Types */}
          <Section icon="📊" title="Report Types" accent="cyan">
            <p className="text-xs text-slate-500">
              Daily Summary is always sent. Enable additional reports below.
            </p>
            <div className="space-y-3">
              <CheckToggle
                label="Detailed + Alerts"
                desc="Full transaction list + client warnings"
                checked={form.chkDetailed}
                onChange={e => toggle('chkDetailed', e.target.checked, setForm)}
              />
              <CheckToggle
                label="Employee Attendance"
                desc="Clock-in/out records"
                checked={form.chkAttendance}
                onChange={e => toggle('chkAttendance', e.target.checked, setForm)}
                accent="indigo"
              />
              <CheckToggle
                label="Employee Sales"
                desc="Individual sales + future appointments"
                checked={form.chkEmployee}
                onChange={e => toggle('chkEmployee', e.target.checked, setForm)}
                accent="emerald"
              />
              <CheckToggle
                label="ZATCA Compliance"
                desc="Invoice submission status"
                checked={form.chkZATCA}
                onChange={e => {
  const checked = e.target.checked;
  setForm(prev => ({
    ...prev,
    chkZATCA: checked,
    zatcaStart: checked && !prev.zatcaStart
      ? new Date().toISOString().split('T')[0]
      : prev.zatcaStart,
  }));
}}
                accent="amber"
              />
            </div>

            {/* ZATCA start date — shown only when enabled */}
            {form.chkZATCA && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mt-2 space-y-2 animate-fadeIn">
                <Field label="ZATCA Go-Live Date" icon="📆" error={errors.zatcaStart}>
                  <Input
                    type="date"
                    value={form.zatcaStart}
                    onChange={e => {
                      setForm(prev => ({ ...prev, zatcaStart: e.target.value }));
                      if (errors.zatcaStart) setErrors(prev => { const n = {...prev}; delete n.zatcaStart; return n; });
                    }}
                  />
                </Field>
                <p className="text-xs text-amber-700">
                  Only invoices after this date are checked for ZATCA submission.
                </p>
              </div>
            )}
          </Section>

          {/* Send Now */}
          <Section icon="🚀" title="Manual Trigger" accent="indigo">
            {/* <p className="text-xs text-slate-500">
              Send all enabled reports immediately — mirrors the VB "Send Now" button.
            </p> */}
            <Field label="Override Date" icon="📅">
              <Input
                type="date"
                value={sendDate}
                onChange={e => setSendDate(e.target.value)}
              />
            </Field>
            {sendDate && (
              <p className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                📅 Will generate reports for <strong>{new Date(sendDate + 'T00:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' })}</strong>
              </p>
            )}
            <button
              onClick={handleSendNow}
              disabled={sending}
              className={`w-full px-6 py-3.5 font-bold rounded-xl shadow-lg flex items-center justify-center gap-2.5 transition-all duration-200 ${
                sending
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:shadow-xl hover:scale-105 active:scale-100'
              }`}
            >
              {sending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <span className="text-xl">📨</span>
                  Send Reports Now
                </>
              )}
            </button>
          </Section>
        </div>
      </div>

      {/* ── Save Bar ───────────────────────────────────────────────────────── */}
      <div className="sticky bottom-4 z-40">
        <div className="bg-white/90 backdrop-blur-md border-2 border-slate-200 rounded-2xl shadow-2xl px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {Object.keys(errors).length > 0 ? (
              <span className="text-red-600 font-semibold flex items-center gap-2">
                ⚠️ {Object.keys(errors).length} error{Object.keys(errors).length > 1 ? 's' : ''} — please fix before saving
              </span>
            ) : (
              <span className="text-slate-500">Changes are saved to the database and take effect immediately.</span>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-8 py-3 font-bold rounded-xl shadow-lg flex items-center gap-2.5 transition-all duration-200 ${
              saving
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-xl hover:scale-105 active:scale-100'
            }`}
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <span className="text-lg">💾</span>
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* ── Tailwind animation keyframes (injected once) ───────────────── */}
      <style>{`
        @keyframes slideUp {
          from { opacity:0; transform: translateY(12px); }
          to   { opacity:1; transform: translateY(0); }
        }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
        @keyframes fadeIn {
          from { opacity:0; }
          to   { opacity:1; }
        }
        .animate-fadeIn { animation: fadeIn 0.25s ease-out; }
      `}</style>
    </div>
  );
}